import { useCallback, useState, type CSSProperties, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedWorkspace } from '../../stores/workspaceStore';
import { routeBase } from '../api/collections';
import { Button, TextLink } from '../ui/Button';
import { MenuAnchor, MenuLabel, MenuSurface, useDismiss } from '../ui/Menu';
import { IconAlertCircle, IconAlertTriangle, IconCheck, IconChevronDown, IconSpinnerArc, IconX } from '../ui/icons';
import { dismissUpload } from '../upload/store';
import { fmtLeft, fmtRate, pillText, rowText, useTransferRate, useUploadRows, type RowState, type UploadRow } from '../upload/uploadsList';

type Tone = 'active' | 'warn' | 'danger' | 'done';

/** The pill's tones: working, needs the person, failed, finished. */
const UPLOAD_TONES: Record<Tone, CSSProperties> = {
  active: { border: '1px solid var(--tr-line-2)', background: 'var(--tr-surface)', color: 'var(--tr-ink)' },
  warn: { border: '1px solid #f3dea3', background: '#fff8e6', color: '#5c4400' },
  danger: { border: '1px solid #f3c9c5', background: '#fdf1f0', color: '#8f1d16' },
  done: { border: '1px solid #cfe3d3', background: '#f1f8f2', color: '#2f6b3a' },
};
/** The same tones for a row's words and bar. */
const INK: Record<Tone, string> = { active: '#6b6b70', warn: '#9a6400', danger: '#d9342b', done: '#2f6b3a' };
const BAR: Record<Tone, string> = { active: 'var(--tr-ink)', warn: '#9a6400', danger: '#d9342b', done: '#2f6b3a' };

const toneOf = (s: RowState): Tone =>
  s === 'failed' || s === 'not-imported' ? 'danger' : s === 'interrupted' || s === 'details' ? 'warn' : s === 'imported' ? 'done' : 'active';

function StateIcon({ tone, size = 13 }: { tone: Tone; size?: number }) {
  if (tone === 'danger') return <IconAlertCircle size={size} />;
  if (tone === 'warn') return <IconAlertTriangle size={size} />;
  if (tone === 'done') return <IconCheck size={size} />;
  return <IconSpinnerArc size={size} />;
}

/**
 * The person's uploads, in the top bar: under way here, in another tab or on
 * another device, and finished but not yet looked at. The pill names the one
 * that matters most (a failure, then what waits for the person, then what is
 * moving) and opens the list of all of them. A row leads to where it can be
 * acted on: the files page when it needs the person, its progress otherwise,
 * the study once it is ready. A finished row stays until it is opened or
 * dismissed, here or on any other device. Gone when there is nothing to list.
 */
export function UploadPill() {
  const { current } = useSelectedWorkspace();
  const base = routeBase();
  const rows = useUploadRows({ enabled: true, base, workspace: current?.public_id ?? null });
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close);
  if (!rows.length) return null;
  const top = rows[0];
  const tone = toneOf(top.state);
  return (
    <MenuAnchor>
      <Button
        variant="outlined"
        size={32}
        radius={7}
        fontSize={12.5}
        icon={<StateIcon tone={tone} />}
        title="Your uploads"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(!open);
        }}
        style={UPLOAD_TONES[tone]}
      >
        <span className="tr-hide-sm">{pillText(top)}</span>
        {rows.length > 1 ? (
          <span aria-label={`${rows.length} uploads`} style={{ minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: 'rgba(15,15,16,.08)', fontSize: 11, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            {rows.length}
          </span>
        ) : null}
        <IconChevronDown size={12} />
      </Button>
      {open ? (
        <MenuSurface width={380} right={0} maxHeight="min(480px, calc(100vh - 80px))" style={{ padding: 6 }}>
          <div role="menu" aria-label="Your uploads">
            <MenuLabel>YOUR UPLOADS</MenuLabel>
            {rows.map((r) => (
              <Row key={r.studyId} r={r} close={close} />
            ))}
          </div>
        </MenuSurface>
      ) : null}
    </MenuAnchor>
  );
}

function Row({ r, close }: { r: UploadRow; close: () => void }) {
  const nav = useNavigate();
  const base = routeBase();
  const tone = toneOf(r.state);
  const rate = useTransferRate(r.state === 'sending' ? r.sessionId : null, r.bytes);
  const go = () => {
    close();
    // Opening a finished upload is seeing how it ended.
    if (r.settled) dismissUpload(r.studyId);
    nav(r.to);
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      go();
    }
  };
  const said = rowText(r) + (rate ? ` · ${fmtRate(rate.bps)} · ${fmtLeft(rate.left)}` : '');
  return (
    <div
      role="menuitem"
      tabIndex={0}
      className="tr-hover-track"
      onClick={go}
      onKeyDown={onKey}
      style={{ display: 'flex', flexDirection: 'column', gap: 3, padding: '9px 10px', borderRadius: 6, cursor: 'pointer' }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <span className="tr-ellipsis" style={{ fontSize: 13, fontWeight: 500, color: '#0f0f10', flex: '1 1 auto', minWidth: 0 }}>{r.name}</span>
        <span style={{ fontSize: 12, color: INK[tone], whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {tone === 'active' ? null : <StateIcon tone={tone} size={12} />}
          {said}
        </span>
        {r.settled ? (
          <button
            type="button"
            aria-label={`Dismiss ${r.name}`}
            title="Dismiss"
            className="tr-hover-soft"
            onClick={(e) => {
              e.stopPropagation();
              dismissUpload(r.studyId);
            }}
            style={{ border: 0, background: 'transparent', color: '#8a8a8f', width: 22, height: 22, borderRadius: 5, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flex: 'none' }}
          >
            <IconX size={12} />
          </button>
        ) : null}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, minWidth: 0 }}>
        <span className="tr-ellipsis" style={{ fontSize: 12, color: '#8a8a8f' }}>
          {[r.where, `${r.slides} image${r.slides === 1 ? '' : 's'}`].filter(Boolean).join(' · ')}
        </span>
        {/* Room is made on the Usage page; the refused files then go by themselves (store.ts retryRefused). */}
        {r.outOfStorage && r.storageYours ? (
          <TextLink
            fontSize={12}
            underline
            hover="none"
            onClick={() => {
              close();
              nav(`${base}/account/usage`);
            }}
            style={{ whiteSpace: 'nowrap', flex: 'none' }}
          >
            Free up space
          </TextLink>
        ) : null}
      </div>
      {r.pct !== null ? (
        <div style={{ height: 3, borderRadius: 2, background: '#ececea', marginTop: 3, overflow: 'hidden' }}>
          <div style={{ width: `${Math.max(0, Math.min(100, r.pct))}%`, height: 3, background: BAR[tone], transition: 'width .3s' }} />
        </div>
      ) : null}
    </div>
  );
}
